import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { getCookie, setCookie } from './cookies';

const ConsentContext = createContext(null);

// Bump this whenever the cookie/consent wording changes materially. Users who
// consented under an older version will be asked again so they see the update.
const CONSENT_VERSION = 4;

export function ConsentProvider({ children }) {
  const [consent, setConsentState] = useState(null); // null = undecided
  const [bannerOpen, setBannerOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    const stored = getCookie('bc_consent'); // 'all[:vN]' | 'necessary[:vN]'
    if (stored) {
      const [value, ver] = String(stored).split(':');
      const v = ver ? parseInt(String(ver).replace(/^v/, ''), 10) : 1;
      if ((value === 'all' || value === 'necessary') && v === CONSENT_VERSION) {
        setConsentState({ analytics: value === 'all' });
        return;
      }
    }
    // No consent yet, or an outdated version -> ask again.
    setBannerOpen(true);
  }, []);

  const persist = useCallback((analytics) => {
    const value = analytics ? 'all' : 'necessary';
    setCookie('bc_consent', `${value}:v${CONSENT_VERSION}`, 365);
    setConsentState({ analytics });
    setBannerOpen(false);
    setSettingsOpen(false);
  }, []);

  const acceptAll = useCallback(() => persist(true), [persist]);
  const acceptNecessary = useCallback(() => persist(false), [persist]);
  const openSettings = useCallback(() => setSettingsOpen(true), []);

  return (
    <ConsentContext.Provider value={{ consent, bannerOpen, settingsOpen, setSettingsOpen, acceptAll, acceptNecessary, persist, openSettings }}>
      {children}
    </ConsentContext.Provider>
  );
}

export function useConsent() {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error('useConsent must be used within ConsentProvider');
  return ctx;
}
